import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import colors from '../config/colors';
import { questionnaireData, getTotalQuestions, getQuestionByIndex, getSectionProgress } from '../data/questionnaireData';
import SingleChoiceQuestion from '../components/questions/SingleChoiceQuestion';
import MultiSelectQuestion from '../components/questions/MultiSelectQuestion';
import TextQuestion from '../components/questions/TextQuestion';
import TextAreaQuestion from '../components/questions/TextAreaQuestion';
import TextAreaWithSuggestions from '../components/questions/TextAreaWithSuggestions';
import DragRankQuestion from '../components/questions/DragRankQuestion';
import AutocompleteQuestion from '../components/questions/AutocompleteQuestion';
import NumberedSelectQuestion from '../components/questions/NumberedSelectQuestion';
import SearchableSelectQuestion from '../components/questions/SearchableSelectQuestion';

const QuestionnairePage = () => {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState('');
  
  const totalQuestions = getTotalQuestions(answers);
  const currentQuestion = getQuestionByIndex(currentQuestionIndex, answers);
  const progress = getSectionProgress(currentQuestionIndex, answers);
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  // Get child's name for personalization
  const childName = answers.q2_name || 'your child';

  // Personalize question text AND option labels/subtext with child's name.
  // Options aren't run through replace today, so {childName} leaks into pills
  // for Q17 priority labels.
  const personalizedQuestion = currentQuestion ? {
    ...currentQuestion,
    question: currentQuestion.question.replace(/{childName}/g, childName),
    subtitle: currentQuestion.subtitle?.replace(/{childName}/g, childName),
    placeholder: currentQuestion.placeholder?.replace(/{childName}/g, childName),
    options: Array.isArray(currentQuestion.options)
      ? currentQuestion.options.map((o) => ({
          ...o,
          label: typeof o.label === 'string'
            ? o.label.replace(/{childName}/g, childName)
            : o.label,
          subtext: typeof o.subtext === 'string'
            ? o.subtext.replace(/{childName}/g, childName)
            : o.subtext,
        }))
      : currentQuestion.options,
  } : null;

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setError('');
  };

  const validateCurrentQuestion = () => {
    const answer = answers[personalizedQuestion.id];
    
    if (personalizedQuestion.required) {
      if (!answer || (Array.isArray(answer) && answer.length === 0)) {
        setError('Please answer this question to continue');
        return false;
      }
      
      if (personalizedQuestion.type === 'textarea' && personalizedQuestion.minLength) {
        if (answer.length < personalizedQuestion.minLength) {
          setError(`Please provide a bit more detail (at least ${personalizedQuestion.minLength} characters)`);
          return false;
        }
      }

      if (personalizedQuestion.type === 'drag-rank' || personalizedQuestion.type === 'numbered-select') {
        const minSelect = personalizedQuestion.minSelect || 1;
        const maxSelect = personalizedQuestion.maxSelect || 5;
        
        if (answer.length < minSelect) {
          setError(`Please select at least ${minSelect} ${minSelect === 1 ? 'priority' : 'priorities'}`);
          return false;
        }
        
        if (answer.length > maxSelect) {
          setError(`Please select no more than ${maxSelect} priorities`);
          return false;
        }
      }
    }
    
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentQuestion()) {
      return;
    }

    if (isLastQuestion) {
      handleSubmit();
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      setError('');
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setError('');
    }
  };

  const handleSubmit = async () => {
    // Check if user is coming from parent dashboard
    const parentMobile = localStorage.getItem('parent_mobile');
    const returnToDashboard = localStorage.getItem('return_to_dashboard');
    const rerunChildId = localStorage.getItem('rerun_child_id');
    const rerunChildName = localStorage.getItem('rerun_child_name');

    // If parent is logged in, redirect to Choose Report page
    if (parentMobile) {
      // Navigate to Choose Report with questionnaire data
      navigate('/choose-report', {
        state: {
          questionnaireData: answers,
          childName: rerunChildName || answers.q2_name || 'My Child',
          childId: rerunChildId || null
        }
      });
      
      // Clear rerun flags
      localStorage.removeItem('return_to_dashboard');
      localStorage.removeItem('rerun_child_id');
      localStorage.removeItem('rerun_child_name');
      
      return;
    }

    // Original flow for non-parent users
    // Transform answers to match backend structure
    let userId = sessionStorage.getItem('user_id');
    
    // Demo mode: create temporary user ID if not authenticated
    if (!userId) {
      userId = 'demo_user_' + Date.now();
      sessionStorage.setItem('user_id', userId);
    }

    // Map answers to backend structure
    const payload = transformAnswersToPayload(answers, userId);
    
    // Store in sessionStorage for backup
    sessionStorage.setItem('questionnaire_data', JSON.stringify(payload));
    
    try {
      // Call backend matching API
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/questionnaire/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
        throw new Error(errorData.detail || `Server returned ${response.status}`);
      }

      const result = await response.json();
      
      // Store matches in the format SchoolReportPage expects (legacy fallback)
      const matchesData = {
        top_schools: result.top_schools || [],
        total_matched: result.total_matched || 0
      };
      sessionStorage.setItem('school_matches', JSON.stringify(matchesData));
      sessionStorage.setItem('response_id', result.response_id || '');
      sessionStorage.setItem('child_name', answers.q2_name || 'your child');

      // Prefer the new ParentReportPage when the backend returned a persisted report_id
      if (result.report_id) {
        // Zero-results safety net: only when literally NO schools passed hard
        // filters (budget + curriculum + year group). With even one match the
        // parent goes straight to the report — they shouldn't be blocked just
        // because verified-signal supply is thin in their combination.
        const topCount = (result.top_schools || []).length;
        if (topCount === 0) {
          navigate(`/match-warning/${result.report_id}`);
        } else {
          navigate(`/parent-report/${result.report_id}`);
        }
      } else {
        navigate('/results');
      }
      
    } catch (error) {
      // Show detailed error message
      setError(error.message || 'Unable to generate your report. Please try again.');
    }
  };

  const renderQuestion = () => {
    const answer = answers[personalizedQuestion.id];

    switch (personalizedQuestion.type) {
      case 'single-choice':
        return (
          <SingleChoiceQuestion
            question={personalizedQuestion}
            value={answer}
            onChange={(value) => handleAnswer(personalizedQuestion.id, value)}
          />
        );
      
      case 'multi-select':
        return (
          <MultiSelectQuestion
            question={personalizedQuestion}
            value={answer || []}
            onChange={(value) => handleAnswer(personalizedQuestion.id, value)}
          />
        );
      
      case 'text':
        return (
          <TextQuestion
            question={personalizedQuestion}
            value={answer || ''}
            onChange={(value) => handleAnswer(personalizedQuestion.id, value)}
          />
        );
      
      case 'textarea':
        return (
          <TextAreaQuestion
            question={personalizedQuestion}
            value={answer || ''}
            onChange={(value) => handleAnswer(personalizedQuestion.id, value)}
          />
        );
      
      case 'textarea-suggestions':
        return (
          <TextAreaWithSuggestions
            question={personalizedQuestion}
            value={answer || ''}
            onChange={(value) => handleAnswer(personalizedQuestion.id, value)}
          />
        );
      
      case 'drag-rank':
        return (
          <DragRankQuestion
            question={personalizedQuestion}
            value={answer || []}
            onChange={(value) => handleAnswer(personalizedQuestion.id, value)}
          />
        );
      
      case 'numbered-select':
        return (
          <NumberedSelectQuestion
            question={personalizedQuestion}
            value={answer || []}
            onChange={(value) => handleAnswer(personalizedQuestion.id, value)}
          />
        );
      
      case 'autocomplete':
        return (
          <AutocompleteQuestion
            question={personalizedQuestion}
            value={answer || ''}
            onChange={(value) => handleAnswer(personalizedQuestion.id, value)}
          />
        );
      
      case 'searchable-select':
        return (
          <SearchableSelectQuestion
            question={personalizedQuestion}
            value={answer || ''}
            onChange={handleAnswer}
          />
        );
      
      default:
        return <div>Unknown question type</div>;
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: colors.bgPage,
      padding: '12px 20px 20px'
    }}>
      {/* Progress Bar */}
      <div style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        height: '4px', 
        background: colors.borderLight,
        zIndex: 100
      }}>
        <motion.div
          style={{ 
            height: '100%', 
            background: `linear-gradient(90deg, ${colors.brandPrimary}, ${colors.brandAccent})`,
          }}
          initial={{ width: '0%' }}
          animate={{ width: `${progress.overallProgress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      {/* Header */}
      <div style={{ 
        maxWidth: '800px', 
        margin: '20px auto 16px',
        textAlign: 'center'
      }}>
        <div style={{ 
          fontSize: '0.8rem', 
          fontWeight: '600', 
          color: colors.brandPrimary,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: '6px'
        }}>
          {String(progress.sectionTitle || '').replace(/{childName}/g, childName)} • Question {currentQuestionIndex + 1} of {totalQuestions}
        </div>
        <div style={{ 
          fontSize: '0.85rem', 
          color: colors.textMuted 
        }}>
          {progress.overallProgress}% Complete
        </div>
      </div>

      {/* Question Container */}
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            {renderQuestion()}
          </motion.div>
        </AnimatePresence>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              marginTop: '24px',
              padding: '16px 20px',
              background: '#FEE2E2',
              border: '2px solid #FCA5A5',
              borderRadius: '8px',
              color: '#991B1B',
              fontSize: '0.95rem',
              fontWeight: '500',
              textAlign: 'center'
            }}
          >
            {error}
          </motion.div>
        )}

        {/* Navigation Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '16px', 
          marginTop: '40px',
          justifyContent: 'space-between'
        }}>
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            style={{
              background: 'transparent',
              color: colors.brandPrimary,
              border: `2px solid ${colors.brandPrimary}`,
              borderRadius: '10rem',
              padding: '14px 28px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: currentQuestionIndex === 0 ? 0.3 : 1,
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => currentQuestionIndex > 0 && (e.target.style.background = colors.bgAccent)}
            onMouseOut={(e) => (e.target.style.background = 'transparent')}
          >
            <ArrowLeft size={18} />
            BACK
          </button>

          <button
            onClick={handleNext}
            style={{
              background: colors.brandPrimary,
              color: colors.textInverse,
              border: 'none',
              borderRadius: '10rem',
              padding: '14px 32px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease',
              boxShadow: `0 4px 16px ${colors.brandPrimary}40`
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'scale(1.02)';
              e.target.style.boxShadow = `0 6px 20px ${colors.brandPrimary}60`;
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = `0 4px 16px ${colors.brandPrimary}40`;
            }}
          >
            {isLastQuestion ? (
              <>
                FINISH &amp; SEE RESULTS
                <Check size={18} />
              </>
            ) : (
              <>
                NEXT
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper function to transform answers to backend payload
const transformAnswersToPayload = (answers, userId) => {
  // Helper function to derive school level from grade
  const getSchoolLevel = (grade) => {
    const gradeNum = parseInt(grade);
    if (gradeNum <= 6) return 'primary';
    if (gradeNum <= 11) return 'secondary';
    return 'sixth_form';
  };

  // Merge primary / secondary / new-student grade fields into a single value.
  const grade =
    answers.q1_grade_primary ||
    answers.q1_grade_secondary ||
    answers.q1_grade_new_student ||
    1;

  // school_level comes from q1_school_level; map 'not_started' → derived from grade
  const rawSchoolLevel = answers.q1_school_level || '';
  const schoolLevel = rawSchoolLevel === 'not_started'
    ? getSchoolLevel(grade)
    : (rawSchoolLevel || getSchoolLevel(grade));

  // Determine current school (empty for new students). Source depends on
  // situation + school_type combination captured in section 1.
  const currentSchool = rawSchoolLevel === 'not_started'
    ? 'Not yet started school'
    : (answers.q1_current_school_international_kl ||
       answers.q1_current_school_international_overseas ||
       answers.q1_current_school_other_kl ||
       answers.q1_current_school_other_overseas ||
       '');

  // The school-type choice the parent made (used for both grade/level routing
  // AND backend parent_signals gating for KL international parents).
  const schoolTypePreference = answers.q1_school_type || answers.q1_school_type_new_student || '';

  return {
    user_id: userId,
    parent_intake: {
      situation: answers.q1_situation || '',
      location: answers.q21_location || '',
      school_level: schoolLevel,
      is_new_student: rawSchoolLevel === 'not_started',
      moving_from_country: answers.q1_country_moving_from || null,
      school_type: schoolTypePreference
    },
    child_profile: {
      child_name: answers.q2_name || '',
      current_grade: grade,
      school_level: schoolLevel,
      academic_snapshot: answers.q2_academic || '',
      strongest_subjects: answers.q2_strongest_subjects || [],
      learning_differences: [],
      interests: answers.q2_interests || [],
      social_style: answers.q2_social || ''
    },
    tuition_spend: {
      total_monthly: 0,
      total_yearly: 0
    },
    diagnostic_questions: {
      current_school: currentSchool,
      current_grade: grade,
      university_destinations: answers.q18_university || [],
      curriculum_priority: answers.q19_curriculum || 'Flexible',
      priorities_ranked: answers.q17_priorities || [],
      budget: answers.q20_budget || '',
      location_preference: answers.q21_location || '',
      school_type_preference: schoolTypePreference,

      // Enhanced algorithm fields (Q15, Q16, Q22)
      whats_working: answers.q15_whats_working || [],
      whats_not_working: answers.q16_not_working || [],
      hopes_concerns: answers.q22_final_thoughts || '',

      // Context for parent_signals persistence (only KL international parents)
      situation: answers.q1_situation || '',
      school_type: schoolTypePreference,
      // The KL-picked school (autocomplete-matched from /api/schools/list)
      current_school_kl_international: answers.q1_current_school_international_kl || ''
    }
  };
};

export default QuestionnairePage;
